import React, { useState, useEffect, useCallback } from 'react';
import { api, API_BASE, logHttpError } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { shopItemSemanticKey, uniqueById } from '../utils/uniqueById';

interface ShopItem {
  id: number;
  name: string;
  description: string;
  price: number;
  image_url: string | null;
  item_type: string;
  bonus_amount: number;
}

interface InventoryItem {
  id: number;
  name: string;
  description: string;
  image_url: string | null;
  item_type: string;
  purchased_at: string;
}

const getApiErrorDetail = (error: unknown, fallback: string): string => {
  if (typeof error !== 'object' || error === null || !('response' in error)) return fallback;

  const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const ShopPage: React.FC = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('sharkly_user_id');
  const isAdmin = localStorage.getItem('sharkly_is_admin') === 'true';

  const [items, setItems] = useState<ShopItem[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [userCoins, setUserCoins] = useState(0);
  const [loading, setLoading] = useState(true);

  // Админ состояния
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemDesc, setNewItemDesc] = useState('');
  const [newItemPrice, setNewItemPrice] = useState(0);
  const [newItemType, setNewItemType] = useState('frame');
  const [newItemBonus, setNewItemBonus] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      // Загружаем товары
      const itemsRes = await api.get('/shop/items');
      setItems(uniqueById<ShopItem>(itemsRes.data as ShopItem[], shopItemSemanticKey));

      // Загружаем инвентарь
      const invRes = await api.get('/shop/my-inventory');
      setInventory(invRes.data);

      // Загружаем баланс пользователя
      const userRes = await api.get('/auth/me');
      setUserCoins(userRes.data.coins || 0);
    } catch (err) {
      logHttpError("Ошибка загрузки данных магазина", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      navigate('/login');
      return;
    }
    void Promise.resolve().then(fetchData);
  }, [fetchData, navigate, userId]);

  const handleBuy = async (itemId: number, price: number) => {
    if (userCoins < price) {
      alert("Недостаточно монет! Заработайте их, участвуя в жизни сообщества.");
      return;
    }

    if (!window.confirm(`Купить этот предмет за ${price} монет?`)) return;

    try {
      const res = await api.post(`/shop/buy/${itemId}`);
      alert(res.data.message);
      fetchData(); // Обновляем баланс и инвентарь
    } catch (err: unknown) {
      alert(getApiErrorDetail(err, "Ошибка при покупке"));
    }
  };

  const handleCreateItem = async () => {
    if (!newItemName || !newItemPrice) {
      alert("Заполните название и цену");
      return;
    }

    try {
      await api.post('/shop/items', {
        name: newItemName,
        description: newItemDesc,
        price: newItemPrice,
        item_type: newItemType,
        bonus_amount: newItemBonus
      });
      alert("Товар успешно создан!");
      setShowAdminModal(false);
      setNewItemName('');
      setNewItemDesc('');
      setNewItemPrice(0);
      setNewItemBonus(0);
      fetchData();
    } catch (err: unknown) {
      alert(getApiErrorDetail(err, "Ошибка создания товара"));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f1115] text-white">
        <div className="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1115] text-white pb-20">
      <main className="max-w-6xl mx-auto p-6">
        {/* Шапка магазина */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
              🛒 Магазин Sharkly
            </h1>
            <p className="text-gray-400 mt-1">Покупай уникальные предметы и улучшай свой профиль</p>
          </div>

          <div className="flex items-center gap-4">
            <div className="bg-[#1a1d24] px-6 py-3 rounded-2xl border border-yellow-500/30 flex items-center gap-3">
              <span className="text-2xl">🪙</span>
              <div>
                <p className="text-xs text-gray-400 uppercase font-bold">Ваш баланс</p>
                <p className="text-xl font-black text-yellow-400">{userCoins} монет</p>
              </div>
            </div>

            {isAdmin && (
              <button
                onClick={() => setShowAdminModal(true)}
                className="px-6 py-3 bg-red-600 hover:bg-red-500 rounded-2xl font-bold transition shadow-[0_0_15px_rgba(220,38,38,0.4)]"
              >
                ⚙️ Добавить товар
              </button>
            )}
          </div>
        </div>

        {/* Витрина товаров */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">🔥 Витрина</h2>
          {items.length === 0 ? (
            <div className="text-center py-20 bg-[#1a1d24] rounded-3xl border border-gray-800 border-dashed">
              <p className="text-4xl mb-3">📦</p>
              <p className="text-gray-400">Пока нет товаров. Загляните позже!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {items.map((item) => {
                const isOwned = inventory.some(inv => inv.id === item.id);

                return (
                  <div key={item.id} className="bg-[#1a1d24] rounded-3xl border border-gray-800 overflow-hidden hover:border-blue-500/50 transition group flex flex-col">
                    <div className="h-48 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center relative">
                      {item.image_url ? (
                        <img src={`${API_BASE}${item.image_url}`} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-6xl group-hover:scale-110 transition">
                          {item.item_type === 'currency_pack' ? '💰' : item.item_type === 'frame' ? '🖼️' : '🏅'}
                        </span>
                      )}
                      {isOwned && (
                        <div className="absolute top-3 right-3 bg-green-500/20 text-green-400 text-xs font-bold px-3 py-1 rounded-full border border-green-500/30">
                          ✓ Куплено
                        </div>
                      )}
                    </div>

                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-bold text-white">{item.name}</h3>
                        <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-lg uppercase font-bold">
                          {item.item_type === 'currency_pack' ? 'Валюта' : item.item_type === 'frame' ? 'Рамка' : 'Предмет'}
                        </span>
                      </div>

                      <p className="text-sm text-gray-400 mb-4 flex-1">{item.description}</p>

                      {item.item_type === 'currency_pack' && item.bonus_amount > 0 && (
                        <p className="text-xs text-green-400 mb-3 font-bold">💡 Дает +{item.bonus_amount} монет</p>
                      )}

                      <button
                        onClick={() => handleBuy(item.id, item.price)}
                        disabled={isOwned}
                        className={`w-full py-3 rounded-xl font-bold transition flex items-center justify-center gap-2 ${
                          isOwned 
                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                            : 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-white shadow-lg shadow-orange-500/20'
                        }`}
                      >
                        {isOwned ? 'В инвентаре' : (
                          <>
                            <span>🪙</span> {item.price}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Инвентарь */}
        <div>
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">🎒 Мой инвентарь</h2>
          {inventory.length === 0 ? (
            <p className="text-gray-500 text-center py-10 bg-[#1a1d24] rounded-3xl border border-gray-800 border-dashed">
              Пока пусто. Купите свой первый предмет!
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {inventory.map((inv) => (
                <div key={inv.id} className="bg-[#1a1d24] p-4 rounded-2xl border border-gray-800 flex items-center gap-4">
                  <div className="w-16 h-16 bg-gray-800 rounded-xl flex items-center justify-center text-2xl shrink-0">
                    {inv.image_url ? (
                      <img src={`${API_BASE}${inv.image_url}`} alt={inv.name} className="w-full h-full object-cover rounded-xl" />
                    ) : (
                      '📦'
                    )}
                  </div>
                  <div>
                    <h4 className="font-bold text-white">{inv.name}</h4>
                    <p className="text-xs text-gray-500">Куплено: {new Date(inv.purchased_at).toLocaleDateString('ru-RU')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Админ-модалка создания товара */}
        {showAdminModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#1a1d24] rounded-3xl border border-gray-700 p-6 max-w-md w-full shadow-2xl">
              <h2 className="text-2xl font-bold mb-6 text-red-400">⚙️ Добавить новый товар</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-400 uppercase font-bold mb-1">Название</label>
                  <input
                    type="text"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-red-500 outline-none"
                    placeholder="Например: Золотая рамка"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-400 uppercase font-bold mb-1">Описание</label>
                  <textarea
                    value={newItemDesc}
                    onChange={(e) => setNewItemDesc(e.target.value)}
                    className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-red-500 outline-none resize-none h-20"
                    placeholder="Описание предмета..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-400 uppercase font-bold mb-1">Цена (монеты)</label>
                    <input
                      type="number"
                      value={newItemPrice}
                      onChange={(e) => setNewItemPrice(Number(e.target.value))}
                      className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-red-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 uppercase font-bold mb-1">Тип</label>
                    <select
                      value={newItemType}
                      onChange={(e) => setNewItemType(e.target.value)}
                      className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-red-500 outline-none"
                    >
                      <option value="frame">Рамка</option>
                      <option value="badge">Бейдж</option>
                      <option value="currency_pack">Пак валюты</option>
                      <option value="other">Другое</option>
                    </select>
                  </div>
                </div>

                {newItemType === 'currency_pack' && (
                  <div>
                    <label className="block text-xs text-gray-400 uppercase font-bold mb-1">Бонус монет (сколько получит пользователь)</label>
                    <input
                      type="number"
                      value={newItemBonus}
                      onChange={(e) => setNewItemBonus(Number(e.target.value))}
                      className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-red-500 outline-none"
                      placeholder="Например: 500"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  onClick={() => setShowAdminModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl font-bold transition"
                >
                  Отмена
                </button>
                <button
                  onClick={handleCreateItem}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-500 rounded-xl font-bold transition shadow-lg shadow-red-600/20"
                >
                  Создать товар
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ShopPage;
